/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/purchase_orders-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-purchase_orders-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/purchase_orders-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, organization_id, vendor_id, project_id, job_id, lot_id, vendor_branch_id, phase_id, status_id, payment_terms_id, is_pick_ticket, is_stock
 * - Sortable fields: order_date, delivery_date, created_at, updated_at, requested_date
 * - Required fields: organization_id, order_date, created_by
 * - Readonly fields: id, created_at, updated_at, trig_approved_by_name, trig_vendor_name, trig_vendor_code, trig_vendor_is_active, trig_vendor_contact_name, trig_vendor_contact_email, trig_vendor_contact_phone, trig_vendor_website, trig_branch_name, trig_branch_code, trig_branch_display_name, trig_branch_contact_name, trig_branch_contact_email, trig_branch_contact_phone, trig_branch_address, trig_branch_city, trig_branch_state, trig_branch_zip_code, trig_branch_is_active, trig_branch_is_primary, trig_vendor_branch_name, trig_phase_name, trig_phase_description, trig_lot_number, trig_created_by_name, trig_status_name, trig_status_code, trig_status_color, trig_job_name, trig_job_number, trig_job_builder_name, trig_job_city, auto_serial_number, trig_payment_terms_name, trig_payment_terms_code
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/purchase_orders-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-purchase_orders-api-client.ts (API client)
 * - client/api/generated_schemas/purchase_orders.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.994Z
 * Source config: config/entities/purchase_orders-entity-config.ts
 * Public fields: 72/72 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrdersClient, PurchaseOrdersQueryOptions } from '../generated_clients/generated-purchase_orders-api-client.js';
import {
  PurchaseOrders,
  PurchaseOrdersInsert,
  PurchaseOrdersUpdate,
  PurchaseOrdersFieldConfig
} from '../generated_schemas/generated-purchase_orders-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/purchase_orders_business_hooks/

// Query keys with enhanced caching strategy
export const purchaseOrdersKeys = {
  all: ['purchase_orders'] as const,
  lists: () => [...purchaseOrdersKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...purchaseOrdersKeys.lists(), { filters }] as const,
  details: () => [...purchaseOrdersKeys.all, 'detail'] as const,
  detail: (id: string) => [...purchaseOrdersKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...purchaseOrdersKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...purchaseOrdersKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...purchaseOrdersKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...purchaseOrdersKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePurchaseOrdersClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PurchaseOrdersClient();
};

/**
 * Hook to get all purchase_orders with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with purchase_orders data
 */
export const usePurchaseOrdersList = (options?: PurchaseOrdersQueryOptions) => {
  const client = usePurchaseOrdersClient();

  return useQuery({
    queryKey: purchaseOrdersKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single purchase_order by ID
 *
 * @param id - The purchase_order ID
 * @returns React Query result with single purchase_order data
 */
export const usePurchaseOrders = (id: string) => {
  const client = usePurchaseOrdersClient();

  return useQuery({
    queryKey: purchaseOrdersKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/purchase_orders
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/purchase_orders_business_hooks/
 */

/**
 * Hook to create a new purchase_order
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: organization_id, order_date, created_by
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, trig_approved_by_name, trig_vendor_name, trig_vendor_code, trig_vendor_is_active, trig_vendor_contact_name, trig_vendor_contact_email, trig_vendor_contact_phone, trig_vendor_website, trig_branch_name, trig_branch_code, trig_branch_display_name, trig_branch_contact_name, trig_branch_contact_email, trig_branch_contact_phone, trig_branch_address, trig_branch_city, trig_branch_state, trig_branch_zip_code, trig_branch_is_active, trig_branch_is_primary, trig_vendor_branch_name, trig_phase_name, trig_phase_description, trig_lot_number, trig_created_by_name, trig_status_name, trig_status_code, trig_status_color, trig_job_name, trig_job_number, trig_job_builder_name, trig_job_city, auto_serial_number, trig_payment_terms_name, trig_payment_terms_code
 *
 * @returns React Query mutation for creating purchase_order
 */
export const useCreatePurchaseOrders = () => {
  const client = usePurchaseOrdersClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PurchaseOrdersInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrdersKeys.all });
    },
  });
};

/**
 * Hook to update an existing purchase_order
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, trig_approved_by_name, trig_vendor_name, trig_vendor_code, trig_vendor_is_active, trig_vendor_contact_name, trig_vendor_contact_email, trig_vendor_contact_phone, trig_vendor_website, trig_branch_name, trig_branch_code, trig_branch_display_name, trig_branch_contact_name, trig_branch_contact_email, trig_branch_contact_phone, trig_branch_address, trig_branch_city, trig_branch_state, trig_branch_zip_code, trig_branch_is_active, trig_branch_is_primary, trig_vendor_branch_name, trig_phase_name, trig_phase_description, trig_lot_number, trig_created_by_name, trig_status_name, trig_status_code, trig_status_color, trig_job_name, trig_job_number, trig_job_builder_name, trig_job_city, auto_serial_number, trig_payment_terms_name, trig_payment_terms_code
 *
 * @returns React Query mutation for updating purchase_order
 */
export const useUpdatePurchaseOrders = () => {
  const client = usePurchaseOrdersClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PurchaseOrdersUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrdersKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrdersKeys.lists() });
    },
  });
};

/**
 * Hook to delete a purchase_order
 *
 * @returns React Query mutation for deleting purchase_order
 */
export const useDeletePurchaseOrders = () => {
  const client = usePurchaseOrdersClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrdersKeys.all });
    },
  });
};

/**
 * Hook to count purchase_orders with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, organization_id, vendor_id, project_id, job_id, lot_id, vendor_branch_id, phase_id, status_id, payment_terms_id, is_pick_ticket, is_stock
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePurchaseOrdersCount = (filter?: Partial<Record<typeof PurchaseOrdersFieldConfig.filterable[number], any>>) => {
  const client = usePurchaseOrdersClient();
  
  return useQuery({
    queryKey: purchaseOrdersKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePurchaseOrdersFieldConfig = () => {
  return PurchaseOrdersFieldConfig;
};